import { useEffect, useRef } from "react";
import { Paperclip, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOpenVerificationEvidence } from "../../license-verification/use-open-verification-evidence";
import { VerificationEvidencePreview } from "../../verification-evidence-preview/verification-evidence-preview";
import type { ReportItem, RunScope } from "../types";
import type { ItemDecisions } from "./use-item-decisions";

const EVIDENCE_FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.docx";

export const NO_EVIDENCE_NUDGE =
  "No evidence was captured for this item. Upload a screenshot or document to support your decision.";

/** A button that opens the file picker and uploads the chosen file to the
 * item. The picker is a visually hidden input so tests and assistive
 * technology can reach it. */
export function ItemUploadButton({
  item,
  title,
  decisions,
  focusRequested,
  onFocused,
  label = "Upload evidence",
}: {
  item: ReportItem;
  title: string;
  decisions: ItemDecisions;
  focusRequested: boolean;
  onFocused: () => void;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusRequested) {
      button.current?.focus();
      onFocused();
    }
  }, [focusRequested, onFocused]);
  return (
    <>
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept={EVIDENCE_FILE_ACCEPT}
        aria-label={`Evidence file for ${title}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) {
            void decisions.uploadFile({ item, file });
          }
        }}
      />
      <Button
        ref={button}
        type="button"
        variant="surface"
        size="sm"
        disabled={decisions.isBusy(item.key)}
        onClick={() => input.current?.click()}
      >
        <Upload className="mr-1.5 size-4" aria-hidden />
        {label}
      </Button>
    </>
  );
}

/** Files staff attached to the item, previewed in the app and withdrawable
 * while the report is editable. */
export function ItemStaffFiles({
  item,
  runId,
  scope,
  canEdit,
  decisions,
}: {
  item: ReportItem;
  runId: string;
  scope: RunScope;
  canEdit: boolean;
  decisions: ItemDecisions;
}) {
  const evidence = useOpenVerificationEvidence({ scope, runId });
  const files = item.decision?.files ?? [];
  if (files.length === 0) {
    return null;
  }
  return (
    <section aria-label="Attached evidence" className="space-y-1.5">
      <h4 className="text-xs font-semibold text-muted-foreground">
        Attached evidence
      </h4>
      <ul className="space-y-1">
        {files.map((file) => (
          <li key={file.id} className="flex items-center gap-2 text-sm">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto min-w-0 gap-1.5 p-0"
              onClick={() =>
                evidence.open({
                  itemKey: item.key,
                  itemFileId: file.id,
                  label: file.fileName,
                })
              }
            >
              <Paperclip className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{file.fileName}</span>
            </Button>
            {canEdit && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label={`Remove ${file.fileName}`}
                disabled={decisions.isBusy(item.key)}
                onClick={() =>
                  void decisions.withdrawFile({ item, fileId: file.id })
                }
              >
                <X className="size-3.5" aria-hidden />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <VerificationEvidencePreview {...evidence} />
    </section>
  );
}
